/**
 * Product Card Collection - Variant Selection Handler
 * Handles variant selection for collection product cards
 * Extends Shopify's native product-form Web Component
 * Note: Wishlist functionality is handled by wishlist.js
 * Note: Form submission is handled by parent product-form.js Web Component
 */

// Wait for product-form to be defined before extending it
function initProductFormCollection() {
  if (!customElements.get('product-form-collection')) {
    // Get the base ProductForm class
    const ProductForm = customElements.get('product-form');

    if (!ProductForm) {
      // product-form not ready yet, wait and retry
      setTimeout(initProductFormCollection, 50);
      return;
    }

    customElements.define(
      'product-form-collection',
      class ProductFormCollection extends ProductForm {
      constructor() {
        super();

        // Ensure cart is properly detected (re-assign after super())
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Initialize after element is connected and rendered
        // Use requestAnimationFrame to ensure DOM is fully ready
        requestAnimationFrame(() => {
          this.initialize();
        });
      }

      /**
       * Initialize the component after DOM is ready
       */
      initialize() {
        // Get the product card and ID
        this.productCard = this.closest('[data-product-id]');
        this.productId = this.productCard?.dataset.productId;

        if (!this.form || !this.productId) {
          return;
        }

        // Load product variants data
        this.variantsData = this.loadProductData();

        if (!this.variantsData) {
          return;
        }

        // Initialize variant selection
        this.initVariantSelection();
      }

      /**
       * Load product variants from JSON script tag
       */
      loadProductData() {
        const dataScript = document.querySelector(`[data-product-json="${this.productId}"]`);
        if (!dataScript) return null;

        try {
          return JSON.parse(dataScript.textContent);
        } catch (error) {
          console.error('Error parsing product data:', error);
          return null;
        }
      }

      /**
       * Initialize variant selection event listeners
       */
      initVariantSelection() {
        // Listen to size option changes
        const sizeInputs = this.form.querySelectorAll('.product-card-collection__size-input');

        sizeInputs.forEach(input => {
          input.addEventListener('change', () => {
            this.onVariantChange();
          });
        });
      }

      /**
       * Handle variant change
       */
      onVariantChange() {
        const selectedOptions = this.getSelectedOptions();
        const variant = this.getVariantFromOptions(selectedOptions);

        if (variant) {
          this.updateVariantInput(variant);
          this.updateSubmitButton(variant);
          this.updateRestockAlert(variant);
        }
      }

      /**
       * Get currently selected options
       */
      getSelectedOptions() {
        const options = [];

        // Get only size radio inputs (product options)
        const selectedInputs = this.form.querySelectorAll('.product-card-collection__size-input:checked');

        selectedInputs.forEach(input => {
          if (input.value) {
            options.push(input.value);
          }
        });

        return options;
      }

      /**
       * Find variant matching selected options
       * @param {Array} options - Array of selected option values (e.g., ['3'] for size 3)
       */
      getVariantFromOptions(options) {
        return this.variantsData.find(variant => {
          // Match all selected option values against variant's option1, option2, option3
          return options.every(selectedValue => {
            return variant.option1 === selectedValue ||
                   variant.option2 === selectedValue ||
                   variant.option3 === selectedValue;
          });
        });
      }

      /**
       * Update hidden variant ID input
       */
      updateVariantInput(variant) {
        const variantInput = this.form.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = !variant.available;
        }
      }

      /**
       * Update submit button state
       */
      updateSubmitButton(variant) {
        const submitButton = this.form.querySelector('.product-card-collection__quick-shop');
        if (!submitButton) return;

        const buttonText = submitButton.querySelector('span');

        if (variant.available) {
          submitButton.disabled = false;
          if (buttonText) {
            buttonText.textContent = 'AJOUTER AU PANIER';
          }
        } else {
          submitButton.disabled = true;
          if (buttonText) {
            buttonText.textContent = window.variantStrings?.soldOut || 'Épuisé';
          }
        }
      }

      /**
       * Show/hide the restock alert overlay on the card image based on
       * the availability of the currently selected variant.
       */
      updateRestockAlert(variant) {
        const alert = this.productCard?.querySelector('[data-restock-alert]');
        if (!alert) return;

        alert.classList.toggle('restock-alert--visible', !variant.available);
        alert.dataset.variantId = variant.id;
      }
    }
    );
  }
}

// Initialize when DOM is ready or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductFormCollection);
} else {
  initProductFormCollection();
}
